import { Component, inject } from '@angular/core';
import { AbstractControl, FormControl, ReactiveFormsModule, ValidationErrors, ValidatorFn } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { TranslateModule } from '@ngx-translate/core';
import { BaseSiteLayoutModule } from '@lib/util';
import { TextModuleCategoryEnum, TextModuleSelectionComponent } from '@lib/shared/text-module';
import { ISourcingWaiverCheck } from '@lib/shared/basket-service';

export class SourcingWaiverDialogData {
    constructor(public readonly check: ISourcingWaiverCheck) {}
}

export class SourcingWaiverDialogResult {
    constructor(public readonly justification: string) {}
}

@Component({
    selector: 'sourcing-waiver-dialog',
    standalone: true,
    imports: [
        MatDialogModule,
        ReactiveFormsModule,
        TranslateModule,
        BaseSiteLayoutModule,
        TextModuleSelectionComponent,
    ],
    templateUrl: './sourcing-waiver-dialog.component.html',
    styleUrl: './sourcing-waiver-dialog.component.scss',
})
export class SourcingWaiverDialogComponent {
    public readonly data = inject<SourcingWaiverDialogData>(MAT_DIALOG_DATA);

    private readonly dialogRef = inject<MatDialogRef<SourcingWaiverDialogComponent, SourcingWaiverDialogResult>>(
        MatDialogRef,
    );

    public readonly justificationControl = new FormControl<string>('', {
        nonNullable: true,
        validators: [trimmedRequiredValidator()],
    });

    protected readonly TextModuleCategoryEnum = TextModuleCategoryEnum;

    public save(): void {
        const justification = this.justificationControl.getRawValue().trim();

        if (!justification) {
            return;
        }

        this.dialogRef.close(new SourcingWaiverDialogResult(justification));
    }

    public close(): void {
        this.dialogRef.close();
    }
}

/**
 * Required validator that treats a whitespace-only value as empty, so `control.invalid` reflects the
 * trimmed emptiness. Lets the template bind `[disabled]="justificationControl.invalid"` instead of
 * calling a method (code-style.md: no method calls in templates). Mirrors the shared
 * `trimmedRequiredValidator` used across the user-management and purchasing-group forms.
 */
function trimmedRequiredValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        const value = typeof control.value === 'string' ? control.value : '';
        return value.trim().length > 0 ? null : { required: true };
    };
}
